import type { SourcePack } from "@desk/core";
import { isValidPublicRelativeAssetPath } from "@desk/core";
import { OMINOUS_AUDIO_ASSET_ID, WARM_AUDIO_ASSET_ID } from "../../content/ids.js";

/** Resolve SourcePack file asset paths against the game public root. */
export function assetUrl(pack: SourcePack, assetId: string): string {
  const entry = pack.assets.find((a) => a.id === assetId);
  if (!entry) {
    throw new Error(`Unknown asset: ${assetId}`);
  }
  if ((entry.sourceKind ?? "file") === "procedural") {
    throw new Error(`Asset ${assetId} is procedural (no public file path): ${entry.sourceUrl}`);
  }
  if (!entry.path || !isValidPublicRelativeAssetPath(entry.path)) {
    throw new Error(`Asset ${assetId} has invalid public-relative path: ${entry.path}`);
  }
  return `/${entry.path}`;
}

let cachedPrefersOgg: boolean | null = null;

/** True when this runtime can decode preferred Ogg Opus audio. */
export function runtimePrefersOggAudio(
  canPlayType: (type: string) => string = (type) => {
    if (typeof Audio === "undefined") return "";
    return new Audio().canPlayType(type);
  },
): boolean {
  if (cachedPrefersOgg != null) return cachedPrefersOgg;
  const opus = canPlayType('audio/ogg; codecs="opus"');
  const ogg = canPlayType("audio/ogg");
  cachedPrefersOgg = Boolean(opus || ogg);
  return cachedPrefersOgg;
}

/** Reset codec preference cache (tests). */
export function resetAudioSourcePreferenceForTests(): void {
  cachedPrefersOgg = null;
}

/**
 * Prefer Ogg Opus when the engine reports support; otherwise use AAC/M4A fallbackPath.
 * Falls back to preferred path when no fallbackPath is declared.
 */
export function playableAudioUrl(
  pack: SourcePack,
  assetId: string,
  canPlayType?: (type: string) => string,
): string {
  const entry = pack.assets.find((a) => a.id === assetId);
  if (!entry) {
    throw new Error(`Unknown asset: ${assetId}`);
  }
  if ((entry.sourceKind ?? "file") === "procedural") {
    throw new Error(`Asset ${assetId} is procedural (no public file path): ${entry.sourceUrl}`);
  }
  if (!entry.path || !isValidPublicRelativeAssetPath(entry.path)) {
    throw new Error(`Asset ${assetId} has invalid public-relative path: ${entry.path}`);
  }
  if (runtimePrefersOggAudio(canPlayType)) {
    return `/${entry.path}`;
  }
  if (entry.fallbackPath) {
    if (!isValidPublicRelativeAssetPath(entry.fallbackPath)) {
      throw new Error(`Asset ${assetId} has invalid fallbackPath: ${entry.fallbackPath}`);
    }
    return `/${entry.fallbackPath}`;
  }
  return `/${entry.path}`;
}

export { OMINOUS_AUDIO_ASSET_ID, WARM_AUDIO_ASSET_ID };
